import { DOCUMENT, ViewportScroller } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import {
  getFirstFocusControlName,
  ScrollService,
} from '@innogy/utils-deprecated';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { FocusAction } from 'ngrx-forms';
import { delay, filter, map, mergeMap, tap } from 'rxjs/operators';
import { FehApiActions } from '@feh/shared-api';

import {
  navigateToProductOverviewPageAction,
  navigateToResultOverviewPageAction,
  selectSolarPanelsFunnelSettings,
} from '../solar-panels-funnel';
import {
  provideCriteriaAndCalculationParametersToSuitabilityCheckAction,
  scrollToSolarPanelsSuitabilityCheckAction,
  solarPanelsSuitabilityCheckFormSubmitAction,
} from './solar-panels-suitability-check.actions';
import { selectSolarPanelsSuitabilityCheckFormState } from './solar-panels-suitability-check.selectors';
import { solarPanelsSuitabilityCheckFormId } from './form-ids';

@Injectable()
export class SolarPanelsSuitabilityCheckEffects {
  private readonly actions$ = inject(Actions);
  private readonly store$ = inject(Store);
  private readonly document: Document = inject(DOCUMENT);
  private readonly scrollService = inject(ScrollService);
  private readonly viewportScroller = inject(ViewportScroller);

  funnelSettings$ = this.store$.select(selectSolarPanelsFunnelSettings);
  formState$ = this.store$.select(selectSolarPanelsSuitabilityCheckFormState);

  constructor() {}

  public readonly provideCriteriaAndParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FehApiActions.getAddressinfoV1.successAction),
      mergeMap((action) => {
        const { calculationParameters, criteria } = action.payload;
        return [
          provideCriteriaAndCalculationParametersToSuitabilityCheckAction({
            calculationParameters,
            criteria,
          }),
        ];
      })
    )
  );

  public readonly scrollToSuitabilityCheckOnSinglePage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FehApiActions.getAddressinfoV1.successAction),
      concatLatestFrom(() => this.funnelSettings$),
      filter(([, { showSuitabilityCheckPage }]) => !!showSuitabilityCheckPage),
      map(scrollToSolarPanelsSuitabilityCheckAction)
    )
  );

  public readonly scrollToSuitabilityCheck$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(scrollToSolarPanelsSuitabilityCheckAction),
        delay(350),
        tap(() => {
          const formElement = this.document.getElementById(
            solarPanelsSuitabilityCheckFormId
          );

          const offset = this.scrollService.getElementVerticalOffset(
            formElement?.closest?.('wl-dynamic-row')
          );

          if (typeof offset !== 'undefined') {
            this.viewportScroller.scrollToPosition([0, offset]);
          }
        })
      ),
    { dispatch: false }
  );

  public readonly focusOnSuitabilityCheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(scrollToSolarPanelsSuitabilityCheckAction),
      delay(450),
      mergeMap(() => {
        return [
          new FocusAction(
            getFirstFocusControlName(
              this.document,
              solarPanelsSuitabilityCheckFormId
            ) ?? ''
          ),
        ];
      })
    )
  );

  public readonly dispatchNavigateToResultOverviewPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(solarPanelsSuitabilityCheckFormSubmitAction),
      concatLatestFrom(() => this.funnelSettings$),
      filter(
        ([, funnelSettings]) =>
          !!funnelSettings.showResultOverviewPage &&
          !funnelSettings.showProductOverviewPage
      ),
      map(([, { resultOverviewPage }]) =>
        navigateToResultOverviewPageAction({
          pageData: resultOverviewPage,
        })
      )
    )
  );

  public readonly dispatchNavigateToProductOverviewPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(solarPanelsSuitabilityCheckFormSubmitAction),
      concatLatestFrom(() => this.funnelSettings$),
      filter(([, funnelSettings]) => !!funnelSettings.showProductOverviewPage),
      map(([, { productOverviewPage }]) =>
        navigateToProductOverviewPageAction({
          pageData: productOverviewPage,
        })
      )
    )
  );
}
